import { useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft } from '@fortawesome/free-solid-svg-icons';
import Button from '../Button';
import { JobDetails } from '../../types/job';
import OffCanvas from '../common/OffCanvas';

// Custom styles for job description HTML content
const jobDescriptionStyles = {
  container: {
    lineHeight: '1.6',
    fontSize: '1rem',
    color: '#333',
    textAlign: 'left'
  }
};

// Add a style block for HTML content
const htmlContentStyle = `
  .job-html-content {
    text-align: left;
  }
  .job-html-content ul, .job-html-content ol {
    padding-left: 1.5rem;
    margin: 0.5rem 0;
    text-align: left;
  }
  .job-html-content li {
    margin: 0.25rem 0;
    text-align: left;
  }
  .job-html-content p {
    margin: 0.5rem 0;
    text-align: left;
  }
  .job-html-content h1 {
    font-size: 1.5rem;
    margin: 1rem 0 0.5rem;
    text-align: left;
  }
  .job-html-content h2 {
    font-size: 1.3rem;
    margin: 1rem 0 0.5rem;
    text-align: left;
  }
  .job-html-content h3 {
    font-size: 1.1rem;
    margin: 1rem 0 0.5rem;
    text-align: left;
  }
  .job-html-content strong {
    font-weight: bold;
  }
  .job-html-content a {
    color: #0d6efd;
    text-decoration: underline;
  }
  .job-html-content table {
    border-collapse: collapse;
    width: 100%;
    margin: 1rem 0;
  }
  .job-html-content th {
    border: 1px solid #ddd;
    padding: 8px;
    text-align: left;
    background-color: #f2f2f2;
  }
  .job-html-content td {
    border: 1px solid #ddd;
    padding: 8px;
    text-align: left;
  }
  
  /* Responsive styles */
  @media (max-width: 768px) {
    .job-html-content table {
      display: block;
      overflow-x: auto;
      white-space: nowrap;
    }
    .job-html-content h1 {
      font-size: 1.3rem;
    }
    .job-html-content h2 {
      font-size: 1.2rem;
    }
    .job-html-content h3 {
      font-size: 1.1rem;
    }
  }
`;

interface JobDetailsOffCanvasProps {
  isOpen: boolean;
  onClose: () => void;
  position?: 'left' | 'right';
  jobDetails: JobDetails | null | undefined;
  closeAllOffCanvas?: () => void;
}

const JobDetailsOffCanvas = ({ isOpen, onClose, position = 'right', jobDetails, closeAllOffCanvas }: JobDetailsOffCanvasProps) => {
  const handleCloseButtonClick = () => {
    if (closeAllOffCanvas) {
      closeAllOffCanvas();
    } else {
      onClose();
    }
  };
  
  // Custom footer with button
  const customFooter = (
    <Button
      text="Close"
      variant="outline-secondary"
      onClick={onClose}
    />
  );

  return (
    <>
      {/* Add style tag for HTML content */}
      <style>{htmlContentStyle}</style>
      
      <OffCanvas
        isOpen={isOpen}
        onClose={handleCloseButtonClick}
        position={position}
        title="View Job Details"
        width="60%"
        mobileWidth="100%"
        zIndex={1070}
        footerContent={customFooter}
      >
        <div style={{ textAlign: 'left' }}>
          <button 
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              padding: '0.5rem',
              cursor: 'pointer',
              color: '#0d6efd',
              display: 'flex',
              alignItems: 'center',
              marginBottom: '1rem',
              textAlign: 'left'
            }}
          >
            <FontAwesomeIcon icon={faArrowLeft} style={{ marginRight: '0.5rem' }} />
            Back
          </button>
          
          <h2 style={{ marginTop: 0, textAlign: 'left' }}>{jobDetails?.title}</h2>
          
          {/* Responsive layout for job details info */}
          <div style={{ 
            marginBottom: '2rem', 
            display: 'flex', 
            flexDirection: window.innerWidth < 768 ? 'column' : 'row',
            gap: '1rem',
            textAlign: 'left'
          }}>
            {/* Left Column */}
            <div style={{ flex: 1, marginRight: window.innerWidth < 768 ? 0 : '1rem', textAlign: 'left' }}>
              <div style={{ marginBottom: '1rem', textAlign: 'left' }}>
                <strong>Location:</strong> {jobDetails?.location}
              </div>
              <div style={{ marginBottom: '1rem', textAlign: 'left' }}>
                <strong>Function:</strong> {jobDetails?.function}
              </div>
              <div style={{ marginBottom: '1rem', textAlign: 'left' }}>
                <strong>Industry:</strong> {jobDetails?.industry}
              </div>
              <div style={{ marginBottom: '1rem', textAlign: 'left' }}>
                <strong>Company:</strong> {jobDetails?.companyName}
              </div>
            </div>
            
            {/* Right Column */}
            <div style={{ flex: 1, textAlign: 'left' }}>
              <div style={{ marginBottom: '1rem', textAlign: 'left' }}>
                <strong>Apply URL:</strong> <a href={jobDetails?.hostedApplyUrl} target="_blank" rel="noopener noreferrer" style={{ wordBreak: 'break-all' }}>{jobDetails?.hostedApplyUrl}</a>
              </div>
              <div style={{ marginBottom: '1rem', textAlign: 'left' }}>
                <strong>Contact Email:</strong> {jobDetails?.contactEmail}
              </div>
            </div>
          </div>

          <div style={{ marginBottom: '2rem', textAlign: 'left' }}>
            <h3 style={{ textAlign: 'left' }}>Job Description</h3>
            <div 
              className="job-description-content job-html-content"
              dangerouslySetInnerHTML={{ __html: jobDetails?.description || '' }}
              style={{
                ...jobDescriptionStyles.container,
                textAlign: 'left'
              }}
            />
          </div>

          {/* Only render Required Skills section if we have items */}
          {jobDetails?.requiredSkills 
          && jobDetails?.requiredSkills?.replace(/<[^>]*>/g, '').trim().length > 0 
          && (
            <div style={{ textAlign: 'left' }}>
              <h3 style={{ textAlign: 'left' }}>Job Requirements</h3>
              {/* <ul style={{ textAlign: 'left' }}>
                {jobDetails?.requiredSkills.map((skill, index) => (
                  <li key={index} style={{ textAlign: 'left' }}>{skill}</li>
                ))}
              </ul> */}
              <div 
              className="job-description-content job-html-content"
              dangerouslySetInnerHTML={{ __html: jobDetails?.requiredSkills || '' }}
              style={{
                ...jobDescriptionStyles.container,
                textAlign: 'left'
              }}
            />
            </div>
          )}
        </div>
      </OffCanvas>
    </>
  );
};

export default JobDetailsOffCanvas; 